import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Radio, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DonateModal from "./DonateModal";

const links = [
  { label: "Watch Live", href: "/#live" },
  { label: "Live Mic", href: "/#live-mic" },
  { label: "Episodes", href: "/#episodes" },
  { label: "Videos", href: "/#videos" },
  { label: "Gallery", href: "/#gallery" },
  { label: "About", href: "/about" },
  { label: "Be a Guest", href: "/about#be-a-guest" },
  { label: "Ask AI", href: "/#ask" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#050B14]/95 backdrop-blur-md border-b border-[#0066FF]/20" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <a href="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img src="/osb-logo.png" alt="The Other Side of Blue logo" className="h-9 w-9 rounded-lg object-cover" />
          <div className="leading-none">
            <div className="font-display text-lg tracking-wide">
              THE OTHER SIDE OF <span className="text-[#0066FF]">BLUE</span>
            </div>
            <div className="font-mono2 text-[9px] tracking-[0.3em] text-[#94A3B8]">ST. LOUIS</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className="font-cond font-semibold uppercase tracking-wide text-sm text-[#F5F7FA]/80 hover:text-[#0066FF] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/#subscribe"
            data-testid="nav-subscribe-btn"
            className="rounded-full bg-[#0066FF] hover:bg-[#2B82FF] transition-colors px-5 py-2 font-cond font-bold uppercase tracking-wide text-sm text-white blue-box-glow"
          >
            Subscribe
          </a>
          <DonateModal variant="solid" testid="nav-donate-btn" />
          <button
            onClick={() => navigate(user ? "/admin" : "/login")}
            data-testid="nav-admin-btn"
            className="flex items-center gap-1.5 text-[#94A3B8] hover:text-[#FF6B00] transition-colors font-cond uppercase text-sm font-semibold"
          >
            <Lock className="h-4 w-4" /> Admin
          </button>
        </nav>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0C1424] border-t border-[#0066FF]/20 px-5 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="font-cond font-semibold uppercase tracking-wide text-[#F5F7FA]/85"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/#subscribe"
            onClick={() => setOpen(false)}
            className="rounded-full bg-[#0066FF] px-5 py-2.5 text-center font-cond font-bold uppercase text-white"
          >
            Subscribe
          </a>
          <DonateModal variant="solid" testid="nav-mobile-donate-btn" className="justify-center" />
          <button
            onClick={() => { setOpen(false); navigate(user ? "/admin" : "/login"); }}
            className="flex items-center justify-center gap-1.5 text-[#FF6B00] font-cond uppercase font-semibold"
          >
            <Lock className="h-4 w-4" /> Admin Access
          </button>
        </div>
      )}
    </header>
  );
}
